/**
 * Ticket and case references (issue #88). Every ticket-number badge opens a small menu: copy the number, copy
 * the Patchbay case link, open the ticket in Zoho Desk. Every case reference is a real `<a href="/case/…">`, so
 * Ctrl/Cmd-click and middle-click open a new tab, with the same menu beside it.
 *
 * The badge swallows its own clicks, double-clicks and keys (and those of its portalled menu, which bubble through
 * the React tree), so a click on the badge never also opens the row it sits in.
 */
import { chipVariants, cn, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, toast } from '@patchbay/ui'
import { QueryClientContext } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { ChevronDown, Copy, ExternalLink, FolderOpen, Link2, Undo2 } from 'lucide-react'
import * as React from 'react'
import { sessionQuery } from '../lib/api/queries'
import { goToCase } from '../lib/go-to'

/** Fallback when /me has not answered: Ewee's Desk portal + department (the API setting DESK_AGENT_TICKET_URL wins). */
export const DESK_TICKET_URL_PREFIX = 'https://desk.zoho.com/agent/eweeinc/ewee-helpdesk/tickets/details/'

export function deskTicketUrl(deskTicketId: string, prefix: string = DESK_TICKET_URL_PREFIX): string {
  const base = prefix.endsWith('/') ? prefix : `${prefix}/`
  return `${base}${encodeURIComponent(deskTicketId)}`
}

export function caseUrl(caseNumber: string, origin: string = typeof window === 'undefined' ? '' : window.location.origin): string {
  return `${origin}/case/${encodeURIComponent(caseNumber)}`
}

/** navigator.clipboard where the browser allows it, else a hidden textarea + execCommand. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the legacy path (insecure context, permission denied)
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

async function copyWithToast(text: string, what: string) {
  if (await copyText(text)) toast(`Copied ${what}`, { description: text })
  else toast(`Could not copy ${what}`, { description: text })
}

export interface TicketListItem {
  number: string | number
  role: string
  deskTicketId?: string
  joinKey?: string
  autoJoinUndo?: boolean
}

/**
 * The case-header ticket breadcrumb (#329). The trigger names the primary ticket and count; the one dropdown lists
 * every linked ticket with its type and its own copy/open controls. Ticket details render nowhere else in the header.
 */
export function TicketListMenu({
  tickets,
  caseNumber,
  onUndoAutoJoin,
}: {
  tickets: TicketListItem[]
  caseNumber: string
  onUndoAutoJoin?: (ticket: TicketListItem) => void
}) {
  const prefix = useDeskPrefix()
  const primary = tickets[0]
  if (!primary) return null
  const more = tickets.length - 1
  return (
    <Menu>
      <MenuTrigger
        aria-label={`Ticket #${primary.number}, ${tickets.length} linked ticket${tickets.length === 1 ? '' : 's'}`}
        className="inline-flex min-w-0 cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-foreground hover:bg-muted"
      >
        <span className="shrink-0">Ticket</span>
        <span className="tabular shrink-0 font-mono text-primary">#{primary.number}</span>
        {more > 0 ? <span className="truncate text-subtle">+{more} more</span> : null}
        <ChevronDown aria-hidden className="size-3 shrink-0 text-subtle" />
      </MenuTrigger>
      <MenuContent align="start" className="max-h-96 w-72 overflow-y-auto p-1">
        {tickets.map((ticket, index) => (
          <React.Fragment key={`${ticket.deskTicketId ?? ticket.number}:${ticket.role}`}>
            {index > 0 ? <MenuSeparator /> : null}
            <MenuLabel>
              <span data-ticket-menu-row className="flex min-w-0 items-center gap-2">
                <span className="tabular shrink-0 font-mono text-sm text-foreground">#{ticket.number}</span>
                <span className="min-w-0 truncate">{ticket.role || 'other'}</span>
              </span>
            </MenuLabel>
            <MenuItem
              aria-label={`Copy ticket #${ticket.number} number`}
              title="Copy number"
              onClick={() => void copyWithToast(`#${ticket.number}`, 'number')}
            >
              <Copy /> Copy
            </MenuItem>
            <MenuItem
              aria-label={`Copy link for ticket #${ticket.number}`}
              title={ticket.deskTicketId ? 'Copy Zoho Desk ticket link' : 'Copy Patchbay case link'}
              onClick={() =>
                void copyWithToast(
                  ticket.deskTicketId ? deskTicketUrl(ticket.deskTicketId, prefix()) : caseUrl(caseNumber),
                  ticket.deskTicketId ? 'Desk link' : 'link',
                )
              }
            >
              <Link2 /> Link
            </MenuItem>
            {ticket.deskTicketId ? (
              <MenuItem
                aria-label={`Open ticket #${ticket.number} in Zoho Desk`}
                title="Open in Zoho Desk"
                onClick={() => window.open(deskTicketUrl(ticket.deskTicketId ?? '', prefix()), '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink /> Open
              </MenuItem>
            ) : null}
            {ticket.autoJoinUndo && onUndoAutoJoin ? (
              <MenuItem
                aria-label={`Move Desk #${ticket.number} off this case`}
                title={ticket.joinKey ? `Linked by ${ticket.joinKey}; undoable for 7 days` : 'Undoable for 7 days'}
                className="text-primary"
                onClick={() => onUndoAutoJoin(ticket)}
              >
                <Undo2 /> Undo
              </MenuItem>
            ) : null}
          </React.Fragment>
        ))}
      </MenuContent>
    </Menu>
  )
}

function useDeskPrefix(): () => string {
  // The context, not useQueryClient(): a badge rendered outside a provider (kit stories, tests) still works.
  const qc = React.useContext(QueryClientContext)
  // Read at click time from the session cache: no extra fetch per badge.
  return () => qc?.getQueryData(sessionQuery().queryKey)?.me?.desk_ticket_url_prefix || DESK_TICKET_URL_PREFIX
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation()

export interface RefMenuProps {
  /** Desk ticket number, shown as `#23778`. */
  ticketNumber?: string | number
  /** Patchbay case number (C-00107): enables Copy link. */
  caseNumber?: string
  /** Desk's internal ticket id: enables Open in Zoho Desk. */
  deskTicketId?: string | null
}

function RefMenuItems({
  ticketNumber,
  caseNumber,
  deskTicketId,
  prefix,
  openCase,
}: RefMenuProps & { prefix: () => string; openCase?: () => void }) {
  const label = ticketNumber !== undefined && ticketNumber !== '' ? `#${ticketNumber}` : caseNumber
  return (
    <>
      {label ? (
        <MenuItem onClick={() => void copyWithToast(label, 'number')}>
          <Copy /> Copy number
        </MenuItem>
      ) : null}
      {caseNumber ? (
        <MenuItem onClick={() => void copyWithToast(caseUrl(caseNumber), 'link')}>
          <Link2 /> Copy link
        </MenuItem>
      ) : null}
      {caseNumber && openCase ? (
        <MenuItem onClick={openCase}>
          <FolderOpen /> Open case {caseNumber}
        </MenuItem>
      ) : null}
      {deskTicketId ? (
        <MenuItem onClick={() => window.open(deskTicketUrl(deskTicketId, prefix()), '_blank', 'noopener,noreferrer')}>
          <ExternalLink /> Open in Zoho Desk
        </MenuItem>
      ) : null}
    </>
  )
}

/** A ticket-number badge that opens the reference menu. */
export function TicketBadge({
  number,
  relation,
  title,
  caseNumber,
  deskTicketId,
}: {
  number: string | number
  relation?: string
  title?: string
  caseNumber?: string
  deskTicketId?: string | null
}) {
  const prefix = useDeskPrefix()
  const router = useRouter({ warn: false })
  const openCase = caseNumber && router ? () => void goToCase(router, caseNumber) : undefined
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: an event boundary only; the trigger inside is the control.
    <span className="inline-flex" onClick={stop} onDoubleClick={stop} onKeyDown={stop} data-ticket-badge>
      <Menu>
        <MenuTrigger
          aria-label={`Ticket #${number}: copy or open in Desk`}
          title={title}
          className={cn(
            chipVariants({ tone: 'outline' }),
            'tabular cursor-pointer font-mono text-[11px] hover:border-primary focus-visible:outline-2 focus-visible:outline-ring',
          )}
        >
          #{number}
          {relation ? <span className="font-sans font-normal text-muted-foreground">{relation}</span> : null}
        </MenuTrigger>
        <MenuContent align="start">
          <RefMenuItems ticketNumber={number} caseNumber={caseNumber} deskTicketId={deskTicketId} prefix={prefix} openCase={openCase} />
        </MenuContent>
      </Menu>
    </span>
  )
}

/**
 * A case reference: a real link to /case/<number> (new tab on Ctrl/Cmd/middle click) plus a small menu button
 * with the same items as the ticket badge.
 */
export function CaseRefLink({
  caseNumber,
  deskTicketId,
  children,
  className,
}: {
  caseNumber: string
  deskTicketId?: string | null
  children?: React.ReactNode
  className?: string
}) {
  const prefix = useDeskPrefix()
  // Not <Link>: a case reference also renders outside a router (drawer tests, kit stories) and must stay a real href.
  const router = useRouter({ warn: false })
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: an event boundary only; the link and trigger are the controls.
    <span className="inline-flex items-center gap-0.5" onClick={stop} onDoubleClick={stop} onKeyDown={stop} data-case-ref>
      <a
        href={`/case/${encodeURIComponent(caseNumber)}`}
        onClick={(e) => {
          // Ctrl/Cmd/Shift/middle click keep the browser's new-tab behaviour; a plain click navigates in place.
          if (!router || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
          e.preventDefault()
          // goToCase: the case already on screen reloads and scrolls to the top instead of doing nothing.
          void goToCase(router, caseNumber)
        }}
        className={cn('text-primary hover:underline', className)}
      >
        {children ?? caseNumber}
      </a>
      <Menu>
        <MenuTrigger
          aria-label={`${caseNumber}: copy or open in Desk`}
          className="cursor-pointer rounded p-0.5 text-subtle hover:bg-muted hover:text-foreground"
        >
          <ChevronDown className="size-3" />
        </MenuTrigger>
        <MenuContent align="start">
          <RefMenuItems caseNumber={caseNumber} deskTicketId={deskTicketId} prefix={prefix} />
        </MenuContent>
      </Menu>
    </span>
  )
}
